import type { PlanBuilderMode } from '../hooks/useTrainerWorkoutPlanBuilderState';
import type { WorkoutPlan } from '../../../../types/dataModel';

const STORAGE_KEY_V2 = 'twp_plan_builder_draft_v2';
const STORAGE_KEY_V1 = 'twp_plan_builder_draft_v1';

export interface StoredWorkoutSlot {
  workoutId: string;
  planWorkoutId?: string | null;
  orderInDay: number;
}

export interface StoredPlanDay {
  workouts: StoredWorkoutSlot[];
}

/** @deprecated v1 single-slot shape — migrated on load */
export interface StoredDayAssignment {
  workoutId: string | null;
  planWorkoutId?: string | null;
}

export interface StoredPlanBuilderDraft {
  routeKey: string;
  mode: PlanBuilderMode;
  planName: string;
  difficulty?: WorkoutPlan['difficulty'];
  totalDays: number;
  days: StoredPlanDay[];
  originalName?: string;
  originalDifficulty?: WorkoutPlan['difficulty'];
  originalTotalDays?: number;
  originalDays?: StoredPlanDay[];
}

function migrateV1Days(v1Days: StoredDayAssignment[]): StoredPlanDay[] {
  return v1Days.map((d) => ({
    workouts:
      d.workoutId != null
        ? [
            {
              workoutId: d.workoutId,
              planWorkoutId: d.planWorkoutId ?? null,
              orderInDay: 1,
            },
          ]
        : [],
  }));
}

function normalizeDraft(parsed: Record<string, unknown>): StoredPlanBuilderDraft | null {
  if (typeof parsed.routeKey !== 'string') return null;
  const daysRaw = parsed.days;
  if (!Array.isArray(daysRaw)) return null;

  const first = daysRaw[0] as Record<string, unknown> | undefined;
  const isV1 = first != null && 'workoutId' in first && !('workouts' in first);

  const days = isV1
    ? migrateV1Days(daysRaw as StoredDayAssignment[])
    : (daysRaw as StoredPlanDay[]);

  const originalDaysRaw = parsed.originalDays;
  let originalDays: StoredPlanDay[] | undefined;
  if (Array.isArray(originalDaysRaw)) {
    const origFirst = originalDaysRaw[0] as Record<string, unknown> | undefined;
    const origIsV1 = origFirst != null && 'workoutId' in origFirst && !('workouts' in origFirst);
    originalDays = origIsV1
      ? migrateV1Days(originalDaysRaw as StoredDayAssignment[])
      : (originalDaysRaw as StoredPlanDay[]);
  }

  return {
    routeKey: parsed.routeKey,
    mode: parsed.mode as PlanBuilderMode,
    planName: String(parsed.planName ?? ''),
    difficulty: parsed.difficulty as WorkoutPlan['difficulty'] | undefined,
    totalDays: Number(parsed.totalDays) || 7,
    days,
    originalName: typeof parsed.originalName === 'string' ? parsed.originalName : undefined,
    originalDifficulty: parsed.originalDifficulty as WorkoutPlan['difficulty'] | undefined,
    originalTotalDays:
      typeof parsed.originalTotalDays === 'number' ? parsed.originalTotalDays : undefined,
    originalDays,
  };
}

export function savePlanBuilderDraft(draft: StoredPlanBuilderDraft): void {
  try {
    sessionStorage.setItem(STORAGE_KEY_V2, JSON.stringify(draft));
    sessionStorage.removeItem(STORAGE_KEY_V1);
  } catch {
    // ignore quota / private mode
  }
}

export function loadPlanBuilderDraft(routeKey: string): StoredPlanBuilderDraft | null {
  try {
    const rawV2 = sessionStorage.getItem(STORAGE_KEY_V2);
    if (rawV2) {
      const parsed = normalizeDraft(JSON.parse(rawV2) as Record<string, unknown>);
      if (parsed?.routeKey === routeKey) return parsed;
    }
    const rawV1 = sessionStorage.getItem(STORAGE_KEY_V1);
    if (rawV1) {
      const parsed = normalizeDraft(JSON.parse(rawV1) as Record<string, unknown>);
      if (parsed?.routeKey === routeKey) return parsed;
    }
    return null;
  } catch {
    return null;
  }
}

export function clearPlanBuilderDraft(): void {
  try {
    sessionStorage.removeItem(STORAGE_KEY_V2);
    sessionStorage.removeItem(STORAGE_KEY_V1);
  } catch {
    // ignore
  }
}
